// ============================================================================
// TeamSummaryHeader — header sommaire de la fiche equipe (E1b, F-218)
// ============================================================================
// FRERE de CommitteeSummaryHeader / CompanySummaryHeader / PersonSummaryHeader
// (gabarit nav fiche, standard permanent UI_STANDARDS §Pattern fiche) : tuile +
// titre + chips (departement / effectif) + ligne responsable + bande de metriques
// gap-px (membres, objectifs done/total, affichages ouverts). Tokens @gs/ui
// uniquement (regle #28) — zero valeur CSS en dur.

import { Users } from 'lucide-react';
import { useTranslation } from '../../i18n';

export interface TeamSummaryHeaderProps {
  name: string;
  department: string | null;
  headcount: number;
  directorName: string | null;
  directorEmail: string | null;
  memberCount: number;
  objectivesDone: number;
  objectivesTotal: number;
  postingsCount: number;
}

export function TeamSummaryHeader({
  name, department, headcount, directorName, directorEmail,
  memberCount, objectivesDone, objectivesTotal, postingsCount,
}: TeamSummaryHeaderProps) {
  const { t } = useTranslation();
  const director = directorName ?? directorEmail;

  const metrics = [
    { label: t('gest:equipes.metrics.members'), value: String(memberCount || headcount) },
    {
      label: t('gest:equipes.metrics.objectives'),
      value: objectivesTotal > 0 ? `${objectivesDone} / ${objectivesTotal}` : '—',
    },
    { label: t('gest:equipes.metrics.postings'), value: String(postingsCount) },
  ];

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-start gap-3 px-3.5 py-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400">
          <Users size={20} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-head font-bold text-slate-900 dark:text-slate-50">{name}</h2>
            {department ? (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-chip font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                {department}
              </span>
            ) : null}
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-chip font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              {t('gest:equipes.header.headcount', { n: headcount })}
            </span>
          </div>
          <p className="mt-0.5 truncate text-meta text-slate-500 dark:text-slate-400">
            {director
              ? t('gest:equipes.header.director', { name: director })
              : t('gest:equipes.noDirector')}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-px border-t border-slate-200 bg-slate-200 dark:border-slate-700 dark:bg-slate-700">
        {metrics.map((m) => (
          <div key={m.label} className="bg-white px-3.5 py-2.5 dark:bg-slate-800">
            <div className="text-chip font-semibold uppercase tracking-wide text-slate-400">{m.label}</div>
            <div className="mt-0.5 truncate text-field font-semibold tabular-nums text-slate-800 dark:text-slate-100">
              {m.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
